'use client';

import { type Dayjs } from 'dayjs';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { useTransitionJobMutation } from '@/features/jobs/api/jobsApi';
import { JobActivateModal } from '@/features/jobs/components/new-job';
import { areJobPlanFieldsReadOnly } from '@/features/jobs/utils/jobEditability';
import { BillingAccessCTA } from '@/features/subscription/components';
import { getBillingAccessState } from '@/features/subscription/utils/billingAccess';
import { useGetCurrentUserContextQuery } from '@/features/users/api/usersApi';
import { JobTeamSection } from '@/features/workspaces/components';
import { useWorkspaceRoutes } from '@/hooks/useWorkspaceRoutes';
import {
  ConfirmActionModal,
  DatePicker,
  EmptyState,
  MainButton,
  SecondaryButton,
  SmartInput,
} from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import { ArchiveJobConfirmModal } from '../ArchiveJobConfirmModal';
import { CompleteJobModal } from '../CompleteJobModal';

import styles from './JobDetailsSettingsView.module.scss';

import type { JobSummaryDraft } from '@/features/jobs/components/new-job/types';

type JobDetailsSettingsViewProps = {
  summaryDraft: JobSummaryDraft;
  fieldErrors: Record<string, string>;
  isSaving: boolean;
  stepsCount: number;
  canEditSettings: boolean;
  canTransitionJob: boolean;
  patchDraft: (
    patch: Partial<JobSummaryDraft>,
    options?: { persist?: boolean; forcePersist?: boolean },
  ) => Promise<boolean>;
  patchSettings: (patch: Partial<JobSummaryDraft>) => Promise<boolean>;
};

function toIsoDate(value: Dayjs | null) {
  return value ? value.format('YYYY-MM-DD') : null;
}

export function JobDetailsSettingsView({
  summaryDraft,
  fieldErrors,
  isSaving,
  stepsCount,
  canEditSettings,
  canTransitionJob,
  patchDraft,
  patchSettings,
}: JobDetailsSettingsViewProps) {
  const router = useRouter();
  const workspaceRoutes = useWorkspaceRoutes();
  const [transitionJob] = useTransitionJobMutation();
  const {
    data: currentUserContextResponse,
    isLoading: isBillingAccessLoading,
  } = useGetCurrentUserContextQuery();

  const jobId = summaryDraft.id ?? '';
  const planFieldsReadOnly =
    !canEditSettings || areJobPlanFieldsReadOnly(summaryDraft.status);
  const pendingNameRef = useRef(summaryDraft.name);
  const pendingJobNumberRef = useRef(summaryDraft.jobNumber);

  useEffect(() => {
    pendingNameRef.current = summaryDraft.name;
  }, [summaryDraft.name]);

  useEffect(() => {
    pendingJobNumberRef.current = summaryDraft.jobNumber;
  }, [summaryDraft.jobNumber]);

  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);
  const [isReactivateConfirmOpen, setIsReactivateConfirmOpen] = useState(false);
  const [isMarkCompletedOpen, setIsMarkCompletedOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const isProject = summaryDraft.jobKind === 'project';
  const status = summaryDraft.status;
  const showMarkCompleted = status === 'pending' || status === 'active';
  const showActivate = status === 'pending';
  const showArchive = status === 'active' || status === 'completed';
  const showReactivate = status === 'archived';
  const billingAccessState = getBillingAccessState(
    currentUserContextResponse?.data?.workspace?.plan,
  );
  const hasPremiumAccess =
    isBillingAccessLoading ||
    billingAccessState === 'premium_active' ||
    billingAccessState === 'trial_active';

  const canRunTransition = canTransitionJob && Boolean(jobId);

  if (status === 'pending') {
    return (
      <section className={styles.jobSettingsPage}>
        <EmptyState
          title="Finish setup before changing settings"
          description="Go to Plan to complete required setup for this job."
          actions={
            jobId ? (
              <SecondaryButton
                ariaLabel="Go to Plan"
                onClick={() =>
                  router.push(workspaceRoutes.jobs.detailPlan(jobId))
                }
              >
                Go to Plan
              </SecondaryButton>
            ) : null
          }
        />
      </section>
    );
  }

  const runTransition = async (
    to: 'active' | 'archived' | 'completed',
    successMessage: string,
  ) => {
    if (!jobId || !canRunTransition) return;
    setIsTransitioning(true);
    try {
      const response = await transitionJob({ jobId, to }).unwrap();
      void patchDraft({
        status: response.data?.status ?? to,
      });
      notify({ type: 'success', message: response.message || successMessage });
      return true;
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
      return false;
    } finally {
      setIsTransitioning(false);
    }
  };

  const onArchive = async () => {
    const completed = await runTransition('archived', 'Job archived.');
    if (completed) {
      setIsArchiveConfirmOpen(false);
    }
  };

  const onReactivate = async () => {
    const completed = await runTransition('active', 'Job reactivated.');
    if (completed) {
      setIsReactivateConfirmOpen(false);
    }
  };

  const onMarkCompleted = async (completionDate: Dayjs) => {
    if (!jobId || !canRunTransition) return;

    setIsCompleting(true);
    try {
      const completedAt = toIsoDate(completionDate);
      const response = await transitionJob({
        jobId,
        to: 'completed',
        completed_at: completedAt,
      }).unwrap();

      void patchDraft({
        status: response.data?.status ?? 'completed',
      });
      notify({
        type: 'success',
        message: response.message || 'Job marked as completed.',
      });
      setIsMarkCompletedOpen(false);
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <section className={styles.jobSettingsPage}>
      <div className={styles.jobSettingsPage__panel}>
        <h3 className={styles.jobSettingsPage__sectionTitle}>Job Details</h3>
        <SmartInput
          type="text"
          name="job-settings-name"
          label="Job Name"
          value={summaryDraft.name}
          error={fieldErrors['job-name']}
          readOnly={!canEditSettings}
          onChange={(value) => {
            if (!canEditSettings) return;
            const name = String(value);
            pendingNameRef.current = name;
            void patchDraft({ name });
          }}
          onBlur={() => {
            if (!canEditSettings) return;
            void patchSettings({ name: pendingNameRef.current });
          }}
        />
        <SmartInput
          type="number"
          name="job-settings-number"
          label="Job Number"
          value={summaryDraft.jobNumber}
          error={fieldErrors['job-number']}
          readOnly={planFieldsReadOnly}
          onChange={(value) => {
            if (planFieldsReadOnly) return;
            pendingJobNumberRef.current = value;
            void patchDraft({ jobNumber: value });
          }}
          onBlur={() => {
            if (planFieldsReadOnly) return;
            void patchSettings({ jobNumber: pendingJobNumberRef.current });
          }}
        />
      </div>

      <div className={styles.jobSettingsPage__panel}>
        <h3 className={styles.jobSettingsPage__sectionTitle}>Lifecycle</h3>
        <p className={styles.jobSettingsPage__sectionDescription}>
          Move this job through its lifecycle stages.
        </p>

        <div className={styles.jobSettingsPage__actionsRow}>
          {showMarkCompleted ? (
            <SecondaryButton
              ariaLabel="Mark completed"
              onClick={() => setIsMarkCompletedOpen(true)}
              disabled={!canRunTransition || isTransitioning || isCompleting}
            >
              Mark completed
            </SecondaryButton>
          ) : null}

          {showActivate && hasPremiumAccess ? (
            <MainButton
              ariaLabel="Activate Job"
              onClick={() => setIsActivateModalOpen(true)}
              disabled={!canRunTransition || isBillingAccessLoading || isSaving}
              isLoading={isBillingAccessLoading}
            >
              Activate Job
            </MainButton>
          ) : null}
          {showActivate && !hasPremiumAccess ? (
            <BillingAccessCTA variant="button" reason="activate-job" />
          ) : null}

          {showArchive ? (
            <MainButton
              ariaLabel="Archive Job"
              variant="error"
              onClick={() => setIsArchiveConfirmOpen(true)}
              disabled={!canRunTransition || isTransitioning}
              isLoading={isTransitioning}
            >
              Archive Job
            </MainButton>
          ) : null}

          {showReactivate && hasPremiumAccess ? (
            <MainButton
              ariaLabel="Reactivate Job"
              onClick={() => setIsReactivateConfirmOpen(true)}
              disabled={
                !canRunTransition || isBillingAccessLoading || isTransitioning
              }
              isLoading={isBillingAccessLoading || isTransitioning}
            >
              Reactivate Job
            </MainButton>
          ) : null}
          {showReactivate && !hasPremiumAccess ? (
            <BillingAccessCTA variant="button" reason="activate-job" />
          ) : null}
        </div>
      </div>

      {isProject ? (
        <div className={styles.jobSettingsPage__panel}>
          <h3 className={styles.jobSettingsPage__sectionTitle}>
            Timeline Anchors
          </h3>
          <div className={styles.jobSettingsPage__datesGrid}>
            <DatePicker
              name="job-settings-start-date"
              label="Start Date"
              value={summaryDraft.startDate}
              readOnly={planFieldsReadOnly}
              disabled={planFieldsReadOnly}
              error={fieldErrors['job-start-date']}
              onChange={(value) => {
                if (planFieldsReadOnly) return;
                void patchSettings({ startDate: value });
              }}
            />
            <DatePicker
              name="job-settings-end-date"
              label="End Date"
              value={summaryDraft.endDate}
              readOnly={planFieldsReadOnly}
              disabled={planFieldsReadOnly}
              error={fieldErrors['job-end-date']}
              onChange={(value) => {
                if (planFieldsReadOnly) return;
                void patchSettings({ endDate: value });
              }}
            />
          </div>
        </div>
      ) : null}

      <div className={styles.jobSettingsPage__panel}>
        <JobTeamSection
          readOnly={!canEditSettings}
          jobLeadId={summaryDraft.jobLeadId}
          teamUserIds={summaryDraft.teamUserIds}
          fieldError={fieldErrors['job-lead-id']}
          onJobLeadChange={(jobLeadId) => void patchSettings({ jobLeadId })}
          onTeamUserIdsChange={(teamUserIds) =>
            void patchSettings({ teamUserIds })
          }
        />
      </div>

      <JobActivateModal
        open={isActivateModalOpen}
        onClose={() => setIsActivateModalOpen(false)}
        summary={summaryDraft}
        totalHours={0}
        durationDays={null}
        deliverablesCount={0}
      />

      <ArchiveJobConfirmModal
        open={isArchiveConfirmOpen}
        isProcessing={isTransitioning}
        onConfirm={onArchive}
        onClose={() => {
          if (isTransitioning) return;
          setIsArchiveConfirmOpen(false);
        }}
      />

      <ConfirmActionModal
        open={isReactivateConfirmOpen}
        title="Reactivate job?"
        description="This will move the job back to active status."
        confirmButtonLabel="Reactivate Job"
        confirmButtonVariant="primary"
        requireConfirmationText={false}
        isProcessing={isTransitioning}
        onConfirm={onReactivate}
        onClose={() => {
          if (isTransitioning) return;
          setIsReactivateConfirmOpen(false);
        }}
      />

      <CompleteJobModal
        open={isMarkCompletedOpen}
        isProcessing={isCompleting}
        initialDate={summaryDraft.endDate}
        onConfirm={onMarkCompleted}
        onClose={() => {
          setIsMarkCompletedOpen(false);
        }}
      />
    </section>
  );
}
